import { ExpandMore } from "@mui/icons-material";
import {
  ClickAwayListener,
  Fade,
  List,
  ListItem,
  ListItemText,
  Popper
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import React, { useRef, useState } from "react";
import { useDataSource } from "./hooks/useDataSources";
import { sortVersionsDescending } from "./versions";

// Same look as the web service version selection in the breadcrumb bar
const useStyles = makeStyles(theme => ({
  root: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    height: "100%"
  },
  versionsList: {
    backgroundColor: "#ebf0f5"
  },
  listItem: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    "&:hover": {
      backgroundColor: "#cdd9e1"
    }
  },
  listItemText: {
    color: "#005578",
    textAlign: "center"
  },
  unpublished: {
    color: "#0000004D"
  },
  expandMore: {
    "&:focus": {
      outline: "none"
    }
  },
  popper: {
    maxHeight: "400px !important",
    overflowX: "hidden",
    overflow: "auto",
    zIndex: 1000,
    boxShadow:
      "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)"
  }
}));

interface DataSourceVersionNavigationProps {
  solutionKey: string;
  dataSourceKey: string;
  version: string;
  onSelectVersion: (version: string) => void;
}

/** Breadcrumb entry of a data source version with a selection of all versions. */
export default function DataSourceVersionNavigation({
  solutionKey,
  dataSourceKey,
  version,
  onSelectVersion
}: DataSourceVersionNavigationProps) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<SVGSVGElement>(null);
  const { data } = useDataSource(solutionKey, dataSourceKey);
  const versions = sortVersionsDescending(
    data?.solutions.solution?.cloudDataSources?.dataSource?.allVersions ?? []
  );

  // The breadcrumb entry is a link, which must not be followed when using the selection
  const stopEvent = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div className={classes.root}>
      <div>{version}</div>
      {versions.length > 1 && (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <div>
            <ExpandMore
              ref={anchorRef}
              className={classes.expandMore}
              onClick={event => {
                stopEvent(event);
                setOpen(previous => !previous);
              }}
              data-testid="data-source-version-navigation"
            />
            <Popper
              open={open}
              anchorEl={anchorRef.current}
              placement="bottom"
              transition
              className={classes.popper}
            >
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={350}>
                  <List component="div" className={classes.versionsList}>
                    {versions.map(item => (
                      <ListItem
                        key={item.version}
                        className={classes.listItem}
                        selected={item.version === version}
                        onClick={(event: React.MouseEvent) => {
                          stopEvent(event);
                          setOpen(false);
                          onSelectVersion(item.version);
                        }}
                      >
                        <ListItemText
                          className={clsx(classes.listItemText, {
                            [classes.unpublished]: !item.isLive
                          })}
                          primary={item.version}
                          title={item.isLive ? undefined : "Unpublished"}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Fade>
              )}
            </Popper>
          </div>
        </ClickAwayListener>
      )}
    </div>
  );
}
